import type { MasterTypingDataExport } from "../durability";
import type { SkillMastery } from "../adaptive";
import type {
  KeyValueStorage,
  SessionStoreDocument,
  StoredTypingSession,
} from "../storage";
import { MAX_STORED_SESSIONS } from "../storage";
import { mergeDungeonCharacters } from "../game/dungeon-character";
import type { PlatformIdentityState } from "./types";
import { createGuestPlatformState } from "./sync";

export const ACCOUNT_DEVICE_ID_KEY = "masterTyping:account-device-id:v1";
export const ACCOUNT_SYNC_METADATA_KEY = "masterTyping:account-sync-metadata:v1";
export const MAX_ACCOUNT_SNAPSHOT_BYTES = 5 * 1024 * 1024;

export type AccountSyncSection =
  | "profile"
  | "preferences"
  | "sessions"
  | "legacyStatistics"
  | "gameProgression";

export const ACCOUNT_SYNC_SECTIONS: readonly AccountSyncSection[] = [
  "profile",
  "preferences",
  "sessions",
  "legacyStatistics",
  "gameProgression",
] as const;

export type AccountSectionTimestamps = Record<AccountSyncSection, string>;

export interface AccountUser {
  id: string;
  email: string | null;
  displayName: string | null;
}

export interface AccountSyncEnvelope {
  format: "mastertyping.account-sync";
  schemaVersion: 1;
  deviceId: string;
  generatedAt: string;
  sectionUpdatedAt: AccountSectionTimestamps;
  data: MasterTypingDataExport;
}

export interface AccountSnapshotRecord {
  deviceId: string;
  sourceUpdatedAt: string;
  updatedAt: string;
  envelope: AccountSyncEnvelope;
}

export interface AccountProvider {
  getCurrentUser(): Promise<AccountUser | null>;
  onAuthStateChange(
    listener: (user: AccountUser | null) => void,
  ): () => void;
  signInWithEmail(email: string, redirectTo: string): Promise<void>;
  signOut(): Promise<void>;
  listSnapshots(userId: string): Promise<AccountSnapshotRecord[]>;
  upsertSnapshot(userId: string, envelope: AccountSyncEnvelope): Promise<void>;
  deleteSnapshots(userId: string): Promise<void>;
}

export interface AccountDataAdapter {
  readExport(): MasterTypingDataExport;
  applyEnvelope(envelope: AccountSyncEnvelope): void | Promise<void>;
}

export interface AccountSyncServiceOptions {
  isOnline?: () => boolean;
  now?: () => Date;
  createDeviceId?: () => string;
  syncDelayMs?: number;
}

interface StoredSyncMetadata {
  schemaVersion: 1;
  sectionUpdatedAt: AccountSectionTimestamps;
  lastSyncedAt: string | null;
}

export interface MergeAccountSyncResult {
  envelope: AccountSyncEnvelope;
  acceptedEnvelopes: number;
  rejectedEnvelopes: number;
}

const EPOCH = "1970-01-01T00:00:00.000Z";

function validIso(value: unknown, fallback = EPOCH): string {
  if (typeof value !== "string") return fallback;
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? fallback : new Date(timestamp).toISOString();
}

function clone<T>(value: T): T {
  return structuredClone(value);
}

function emptySectionTimestamps(): AccountSectionTimestamps {
  return {
    profile: EPOCH,
    preferences: EPOCH,
    sessions: EPOCH,
    legacyStatistics: EPOCH,
    gameProgression: EPOCH,
  };
}

function normalizedSectionTimestamps(
  value: unknown,
  sessionFallback = EPOCH,
): AccountSectionTimestamps {
  const candidate =
    value && typeof value === "object"
      ? (value as Partial<AccountSectionTimestamps>)
      : {};
  return {
    profile: validIso(candidate.profile),
    preferences: validIso(candidate.preferences),
    sessions: validIso(candidate.sessions, sessionFallback),
    legacyStatistics: validIso(candidate.legacyStatistics),
    gameProgression: validIso(candidate.gameProgression),
  };
}

function seedSectionTimestamps(
  data: MasterTypingDataExport,
): AccountSectionTimestamps {
  const timestamp = validIso(data.exportedAt, new Date().toISOString());
  const profile = data.data.profile;
  const preferences = data.data.preferences;
  const progression = data.data.gameProgression;
  const hasProfile = Boolean(
    profile.goal ||
      profile.hasCompletedOnboarding ||
      profile.latestRecommendation,
  );
  const hasPreferences = Boolean(
    preferences.telemetryEnabled ||
      preferences.highContrast ||
      preferences.reducedMotion ||
      preferences.fontScale !== 1 ||
      preferences.gamePresentation,
  );
  const hasLegacyEvidence =
    data.data.sessionStore.sessions.length > 0 ||
    JSON.stringify(data.data.legacyStatistics ?? {}).match(/[1-9]/) !== null;
  const hasGameProgress = Boolean(
    progression.tutorialCompleted ||
      Object.keys(progression.records).length > 0 ||
      Object.keys(progression.dungeonRecords).length > 0 ||
      progression.dungeonCharacter ||
      progression.unlockedChampionIds.some((id) => id !== "wizard") ||
      progression.selectedChampionId !== "wizard",
  );
  return {
    profile: hasProfile ? timestamp : EPOCH,
    preferences: hasPreferences ? timestamp : EPOCH,
    sessions: validIso(data.data.sessionStore.updatedAt),
    legacyStatistics: hasLegacyEvidence ? timestamp : EPOCH,
    gameProgression: hasGameProgress ? timestamp : EPOCH,
  };
}

function safeDeviceId(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length >= 8 &&
    value.length <= 128 &&
    /^[a-zA-Z0-9_-]+$/.test(value)
  );
}

function isPortableExport(value: unknown): value is MasterTypingDataExport {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<MasterTypingDataExport>;
  return (
    candidate.format === "mastertyping.user-data" &&
    candidate.schemaVersion === 2 &&
    Boolean(candidate.data) &&
    typeof candidate.data === "object" &&
    Boolean(candidate.data.sessionStore) &&
    candidate.data.sessionStore.schemaVersion === 2
  );
}

export function validateAccountSyncEnvelope(
  value: unknown,
): value is AccountSyncEnvelope {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<AccountSyncEnvelope>;
  if (
    candidate.format !== "mastertyping.account-sync" ||
    candidate.schemaVersion !== 1 ||
    !safeDeviceId(candidate.deviceId) ||
    validIso(candidate.generatedAt, "") === "" ||
    !candidate.sectionUpdatedAt ||
    !isPortableExport(candidate.data)
  ) {
    return false;
  }
  if (
    ACCOUNT_SYNC_SECTIONS.some(
      (section) => validIso(candidate.sectionUpdatedAt?.[section], "") === "",
    )
  ) {
    return false;
  }
  try {
    return new TextEncoder().encode(JSON.stringify(candidate)).byteLength <=
      MAX_ACCOUNT_SNAPSHOT_BYTES;
  } catch {
    return false;
  }
}

export function createAccountSyncEnvelope(
  data: MasterTypingDataExport,
  deviceId: string,
  sectionUpdatedAt: AccountSectionTimestamps,
  now: Date = new Date(),
): AccountSyncEnvelope {
  if (!safeDeviceId(deviceId)) {
    throw new Error("Invalid account sync device ID.");
  }
  const envelope: AccountSyncEnvelope = {
    format: "mastertyping.account-sync",
    schemaVersion: 1,
    deviceId,
    generatedAt: now.toISOString(),
    sectionUpdatedAt: normalizedSectionTimestamps(
      sectionUpdatedAt,
      data.data.sessionStore.updatedAt,
    ),
    data: clone(data),
  };
  if (!validateAccountSyncEnvelope(envelope)) {
    throw new Error("Account sync data exceeded its safety contract.");
  }
  return envelope;
}

function sessionTimestamp(session: StoredTypingSession): number {
  return Date.parse(
    session.summary.completedAt || session.summary.startedAt || EPOCH,
  );
}

function mergeSessionStores(
  documents: readonly SessionStoreDocument[],
  now: Date,
): SessionStoreDocument {
  const sessions = new Map<string, StoredTypingSession>();
  const mastery = new Map<string, SkillMastery>();
  const processedIds = new Set<string>();
  let revision = 0;
  let createdAt = now.toISOString();
  let updatedAt = EPOCH;

  for (const document of documents) {
    revision = Math.max(revision, document.revision);
    if (Date.parse(document.createdAt) < Date.parse(createdAt)) {
      createdAt = validIso(document.createdAt, createdAt);
    }
    if (Date.parse(document.updatedAt) > Date.parse(updatedAt)) {
      updatedAt = validIso(document.updatedAt, updatedAt);
    }
    for (const session of document.sessions) {
      const previous = sessions.get(session.summary.id);
      if (!previous || sessionTimestamp(session) >= sessionTimestamp(previous)) {
        sessions.set(session.summary.id, clone(session));
      }
    }
    for (const item of document.mastery) {
      const previous = mastery.get(item.skillId);
      if (
        !previous ||
        Date.parse(item.lastUpdatedAt) >= Date.parse(previous.lastUpdatedAt)
      ) {
        mastery.set(item.skillId, clone(item));
      }
    }
    document.processedMasterySessionIds.forEach((id) => processedIds.add(id));
  }

  return {
    schemaVersion: 2,
    revision: revision + 1,
    createdAt,
    updatedAt: validIso(updatedAt, now.toISOString()),
    sessions: [...sessions.values()]
      .sort((left, right) => sessionTimestamp(left) - sessionTimestamp(right))
      .slice(-MAX_STORED_SESSIONS),
    mastery: [...mastery.values()].sort((left, right) =>
      left.skillId.localeCompare(right.skillId),
    ),
    processedMasterySessionIds: [...processedIds].slice(-MAX_STORED_SESSIONS),
    migration: null,
  };
}

function uniqueArray(values: readonly unknown[][]): unknown[] {
  const items = new Map<string, unknown>();
  for (const value of values.flat()) {
    try {
      items.set(JSON.stringify(value), clone(value));
    } catch {
      // Non-serializable values cannot enter the portable cloud contract.
    }
  }
  return [...items.values()].slice(-2_000);
}

function mergeProgressValues(values: readonly unknown[]): unknown {
  const defined = values.filter((value) => value !== undefined && value !== null);
  if (defined.length === 0) return null;
  if (defined.every((value) => typeof value === "number")) {
    return Math.max(...(defined as number[]));
  }
  if (defined.every((value) => typeof value === "boolean")) {
    return (defined as boolean[]).some(Boolean);
  }
  if (defined.every(Array.isArray)) {
    return uniqueArray(defined as unknown[][]);
  }
  if (
    defined.every(
      (value) => typeof value === "object" && value !== null && !Array.isArray(value),
    )
  ) {
    const records = defined as Array<Record<string, unknown>>;
    const keys = new Set(records.flatMap((record) => Object.keys(record)));
    return Object.fromEntries(
      [...keys].map((key) => [
        key,
        mergeProgressValues(records.map((record) => record[key])),
      ]),
    );
  }
  return clone(defined.at(-1));
}

function winnerForSection(
  envelopes: readonly AccountSyncEnvelope[],
  section: AccountSyncSection,
): AccountSyncEnvelope {
  return [...envelopes].sort((left, right) => {
    const timeDifference =
      Date.parse(left.sectionUpdatedAt[section]) -
      Date.parse(right.sectionUpdatedAt[section]);
    return timeDifference || left.deviceId.localeCompare(right.deviceId);
  }).at(-1)!;
}

export function mergeAccountSyncEnvelopes(
  localEnvelope: AccountSyncEnvelope,
  remoteValues: readonly unknown[],
  now: Date = new Date(),
): MergeAccountSyncResult {
  if (!validateAccountSyncEnvelope(localEnvelope)) {
    throw new Error("The local account sync document is invalid.");
  }
  const acceptedRemote = remoteValues.filter(validateAccountSyncEnvelope);
  const envelopes = [localEnvelope, ...acceptedRemote];
  const sectionUpdatedAt = emptySectionTimestamps();
  ACCOUNT_SYNC_SECTIONS.forEach((section) => {
    sectionUpdatedAt[section] = winnerForSection(envelopes, section)
      .sectionUpdatedAt[section];
  });

  const profileWinner = winnerForSection(envelopes, "profile");
  const preferencesWinner = winnerForSection(envelopes, "preferences");
  const statisticsWinner = winnerForSection(envelopes, "legacyStatistics");
  const gameWinner = winnerForSection(envelopes, "gameProgression");
  const unlockedChampionIds = [
    ...new Set(
      envelopes.flatMap(
        (envelope) =>
          envelope.data.data.gameProgression.unlockedChampionIds,
      ),
    ),
  ].slice(0, 100);
  const selectedChampionId = unlockedChampionIds.includes(
    gameWinner.data.data.gameProgression.selectedChampionId,
  )
    ? gameWinner.data.data.gameProgression.selectedChampionId
    : unlockedChampionIds[0] ?? "wizard";
  const mergedGameRecords = mergeProgressValues(
    envelopes.map((envelope) => envelope.data.data.gameProgression.records),
  );
  const mergedDungeonRecords = mergeProgressValues(
    envelopes.map(
      (envelope) => envelope.data.data.gameProgression.dungeonRecords,
    ),
  );
  // A character award is keyed by run ID. A generic numeric-max/array-union
  // merge would duplicate conflicting training choices for the same victory.
  // Unsupported character data aborts this merge before any local/remote write.
  const mergedDungeonCharacter = envelopes.reduce(
    (character, envelope) => mergeDungeonCharacters(character, envelope.data.data.gameProgression.dungeonCharacter),
    mergeDungeonCharacters(null, null),
  );
  const metadata = uniqueArray(
    envelopes.map((envelope) => envelope.data.data.customContentMetadata),
  ).filter(
    (item): item is Record<string, unknown> =>
      Boolean(item) && typeof item === "object" && !Array.isArray(item),
  );

  const data: MasterTypingDataExport = {
    ...clone(localEnvelope.data),
    exportedAt: now.toISOString(),
    data: {
      profile: clone(profileWinner.data.data.profile),
      preferences: clone(preferencesWinner.data.data.preferences),
      sessionStore: mergeSessionStores(
        envelopes.map((envelope) => envelope.data.data.sessionStore),
        now,
      ),
      legacyStatistics: clone(
        statisticsWinner.data.data.legacyStatistics,
      ),
      gameProgression: {
        tutorialCompleted: envelopes.some(
          (envelope) => envelope.data.data.gameProgression.tutorialCompleted,
        ),
        records:
          mergedGameRecords &&
          typeof mergedGameRecords === "object" &&
          !Array.isArray(mergedGameRecords)
            ? (mergedGameRecords as Record<string, unknown>)
            : clone(gameWinner.data.data.gameProgression.records),
        dungeonRecords:
          mergedDungeonRecords &&
          typeof mergedDungeonRecords === "object" &&
          !Array.isArray(mergedDungeonRecords)
            ? (mergedDungeonRecords as Record<string, unknown>)
            : clone(gameWinner.data.data.gameProgression.dungeonRecords),
        unlockedChampionIds,
        dungeonCharacter: mergedDungeonCharacter,
        selectedChampionId,
      },
      customContentMetadata: metadata,
    },
  };

  return {
    envelope: createAccountSyncEnvelope(
      data,
      localEnvelope.deviceId,
      sectionUpdatedAt,
      now,
    ),
    acceptedEnvelopes: envelopes.length,
    rejectedEnvelopes: remoteValues.length - acceptedRemote.length,
  };
}

function displayLabel(user: AccountUser): string {
  return user.displayName?.trim() || user.email?.trim() || "Signed in";
}

function authenticatedState(
  user: AccountUser,
  synchronization: PlatformIdentityState["synchronization"],
  online: boolean,
  lastSyncedAt: string | null,
  errorCode: string | null = null,
): PlatformIdentityState {
  const detail = !online
    ? "Offline. New progress is safe on this device and will sync when you reconnect."
    : synchronization === "pending"
      ? "Merging this device with your saved account progress…"
      : synchronization === "synced"
        ? "Progress is saved on this device and synchronized with your account."
        : synchronization === "error"
          ? "Progress is safe on this device. Account sync needs attention."
          : "Signed in. Progress remains local until synchronization finishes.";
  return {
    authentication: "authenticated",
    synchronization,
    online,
    accountId: user.id,
    email: user.email,
    displayName: user.displayName,
    canSignIn: false,
    lastSyncedAt,
    errorCode,
    label: displayLabel(user),
    detail,
  };
}

export class AccountSyncService {
  private adapter: AccountDataAdapter | null = null;
  private user: AccountUser | null = null;
  private state: PlatformIdentityState;
  private readonly listeners = new Set<(state: PlatformIdentityState) => void>();
  private unsubscribeAuth: (() => void) | null = null;
  private synchronization: Promise<void> | null = null;
  private syncTimer: ReturnType<typeof setTimeout> | null = null;
  private applyingRemote = false;
  private started = false;
  private readonly isOnline: () => boolean;
  private readonly now: () => Date;
  private readonly createDeviceId: () => string;
  private readonly syncDelayMs: number;
  private readonly deviceId: string;

  constructor(
    private readonly provider: AccountProvider | null,
    private readonly storage: KeyValueStorage,
    options: AccountSyncServiceOptions = {},
  ) {
    this.isOnline = options.isOnline ?? (() => true);
    this.now = options.now ?? (() => new Date());
    this.createDeviceId =
      options.createDeviceId ??
      (() => `device-${crypto.randomUUID().replaceAll("-", "")}`);
    this.syncDelayMs = options.syncDelayMs ?? 1_500;
    const storedDeviceId = storage.getItem(ACCOUNT_DEVICE_ID_KEY);
    this.deviceId = safeDeviceId(storedDeviceId)
      ? storedDeviceId
      : this.createDeviceId();
    storage.setItem(ACCOUNT_DEVICE_ID_KEY, this.deviceId);
    const guest = createGuestPlatformState(this.isOnline());
    this.state = provider
      ? {
          ...guest,
          canSignIn: true,
          detail: this.isOnline()
            ? "Progress is saved on this device. Sign in to save it to your account and continue on other devices."
            : guest.detail,
        }
      : {
          ...guest,
          authentication: "unavailable",
          synchronization: this.isOnline() ? "unavailable" : "offline",
          canSignIn: false,
          detail: this.isOnline()
            ? "Progress is saved on this device. Account sign-in is not configured for this deployment."
            : guest.detail,
        };
  }

  getState(): PlatformIdentityState {
    return clone(this.state);
  }

  getDeviceId(): string {
    return this.deviceId;
  }

  subscribe(listener: (state: PlatformIdentityState) => void): () => void {
    this.listeners.add(listener);
    listener(this.getState());
    return () => this.listeners.delete(listener);
  }

  async start(adapter: AccountDataAdapter): Promise<void> {
    this.adapter = adapter;
    if (this.started || !this.provider) return;
    this.started = true;
    this.setState({
      ...this.state,
      authentication: "loading",
      synchronization: this.isOnline() ? "pending" : "offline",
      label: "Checking account…",
      detail: "Restoring your secure sign-in state.",
    });
    this.unsubscribeAuth = this.provider.onAuthStateChange((user) => {
      queueMicrotask(() => void this.handleUser(user));
    });
    try {
      await this.handleUser(await this.provider.getCurrentUser());
    } catch {
      const guest = createGuestPlatformState(this.isOnline());
      this.setState({
        ...guest,
        authentication: "error",
        synchronization: "error",
        canSignIn: true,
        errorCode: "auth-initialization-failed",
        detail:
          "Progress is safe on this device. Sign-in could not be restored; try again when online.",
      });
    }
  }

  markChanged(section: AccountSyncSection): void {
    if (this.applyingRemote) return;
    const metadata = this.readMetadata(this.adapter?.readExport());
    metadata.sectionUpdatedAt[section] = this.now().toISOString();
    this.writeMetadata(metadata);
    if (this.user) this.scheduleSync();
  }

  async requestSignIn(email: string, redirectTo: string): Promise<void> {
    if (!this.provider) throw new Error("account-provider-unavailable");
    const normalizedEmail = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      throw new Error("invalid-email");
    }
    if (!this.isOnline()) throw new Error("offline");
    await this.provider.signInWithEmail(normalizedEmail, redirectTo);
  }

  async syncNow(): Promise<void> {
    if (!this.provider || !this.user || !this.adapter) return;
    if (!this.isOnline()) {
      this.setState(
        authenticatedState(
          this.user,
          "offline",
          false,
          this.readMetadata().lastSyncedAt,
        ),
      );
      return;
    }
    if (this.synchronization) return this.synchronization;
    this.synchronization = this.performSync().finally(() => {
      this.synchronization = null;
    });
    return this.synchronization;
  }

  async signOut(): Promise<void> {
    if (!this.provider) return;
    await this.provider.signOut();
    await this.handleUser(null);
  }

  async deleteCloudData(): Promise<void> {
    if (!this.provider || !this.user) return;
    await this.provider.deleteSnapshots(this.user.id);
  }

  handleConnectivityChange(): void {
    if (!this.user) {
      this.setState(this.guestState());
      return;
    }
    if (!this.isOnline()) {
      this.setState(
        authenticatedState(
          this.user,
          "offline",
          false,
          this.readMetadata().lastSyncedAt,
        ),
      );
      return;
    }
    void this.syncNow();
  }

  destroy(): void {
    this.unsubscribeAuth?.();
    this.unsubscribeAuth = null;
    if (this.syncTimer) clearTimeout(this.syncTimer);
    this.syncTimer = null;
    this.listeners.clear();
  }

  private async handleUser(user: AccountUser | null): Promise<void> {
    this.user = user;
    if (!user) {
      this.setState(this.guestState());
      return;
    }
    this.setState(
      authenticatedState(
        user,
        this.isOnline() ? "pending" : "offline",
        this.isOnline(),
        this.readMetadata().lastSyncedAt,
      ),
    );
    await this.syncNow();
  }

  private async performSync(): Promise<void> {
    if (!this.provider || !this.user || !this.adapter) return;
    const metadata = this.readMetadata(this.adapter.readExport());
    this.setState(
      authenticatedState(
        this.user,
        "pending",
        true,
        metadata.lastSyncedAt,
      ),
    );
    try {
      const localEnvelope = createAccountSyncEnvelope(
        this.adapter.readExport(),
        this.deviceId,
        metadata.sectionUpdatedAt,
        this.now(),
      );
      const remote = await this.provider.listSnapshots(this.user.id);
      const merged = mergeAccountSyncEnvelopes(
        localEnvelope,
        remote.map((snapshot) => snapshot.envelope),
        this.now(),
      ).envelope;
      if (remote.length > 0) {
        this.applyingRemote = true;
        try {
          await this.adapter.applyEnvelope(merged);
        } finally {
          this.applyingRemote = false;
        }
      }
      const synchronizedAt = this.now().toISOString();
      this.writeMetadata({
        schemaVersion: 1,
        sectionUpdatedAt: clone(merged.sectionUpdatedAt),
        lastSyncedAt: synchronizedAt,
      });
      const upload = createAccountSyncEnvelope(
        this.adapter.readExport(),
        this.deviceId,
        merged.sectionUpdatedAt,
        this.now(),
      );
      await this.provider.upsertSnapshot(this.user.id, upload);
      this.setState(
        authenticatedState(
          this.user,
          "synced",
          true,
          synchronizedAt,
        ),
      );
    } catch {
      this.setState(
        authenticatedState(
          this.user,
          "error",
          true,
          metadata.lastSyncedAt,
          "sync-failed",
        ),
      );
      throw new Error("account-sync-failed");
    }
  }

  private scheduleSync(): void {
    if (this.syncTimer) clearTimeout(this.syncTimer);
    this.syncTimer = setTimeout(() => {
      this.syncTimer = null;
      void this.syncNow().catch(() => undefined);
    }, this.syncDelayMs);
  }

  private readMetadata(data?: MasterTypingDataExport): StoredSyncMetadata {
    let stored: Partial<StoredSyncMetadata> = {};
    try {
      const parsed = JSON.parse(
        this.storage.getItem(ACCOUNT_SYNC_METADATA_KEY) || "null",
      ) as unknown;
      stored =
        parsed && typeof parsed === "object" && !Array.isArray(parsed)
          ? (parsed as Partial<StoredSyncMetadata>)
          : {};
    } catch {
      stored = {};
    }
    const hasStoredSections =
      stored.schemaVersion === 1 && Boolean(stored.sectionUpdatedAt);
    return {
      schemaVersion: 1,
      sectionUpdatedAt:
        !hasStoredSections && data
          ? seedSectionTimestamps(data)
          : normalizedSectionTimestamps(
              stored.sectionUpdatedAt,
              data?.data.sessionStore.updatedAt,
            ),
      lastSyncedAt:
        stored.lastSyncedAt === null
          ? null
          : validIso(stored.lastSyncedAt, "") || null,
    };
  }

  private writeMetadata(metadata: StoredSyncMetadata): void {
    this.storage.setItem(ACCOUNT_SYNC_METADATA_KEY, JSON.stringify(metadata));
  }

  private setState(state: PlatformIdentityState): void {
    this.state = clone(state);
    for (const listener of this.listeners) listener(this.getState());
  }

  private guestState(): PlatformIdentityState {
    const guest = createGuestPlatformState(this.isOnline());
    return {
      ...guest,
      canSignIn: Boolean(this.provider),
      detail:
        this.provider && this.isOnline()
          ? "Progress is saved on this device. Sign in to save it to your account and continue on other devices."
          : guest.detail,
    };
  }
}
